import { useEffect, type ReactNode } from 'react';
import { m } from 'framer-motion';
import { cn } from '@/lib/cn';
import { AppBar, type AppBarProps } from './AppBar';
import { TabBar, type TabId } from './TabBar';
import { useBackButton } from '@/app/hooks/useBackButton';

export interface ScreenProps extends AppBarProps {
  children: ReactNode;
  /** Активная вкладка нижней навигации; без неё панель скрыта. */
  tab?: TabId;
  /** Убрать боковые отступы (полноэкранные состояния). */
  flush?: boolean;
  className?: string;
  /** Экран без прокрутки: контент занимает высоту вьюпорта. */
  fill?: boolean;
  /**
   * Закреплённая внизу область: поле ввода чата.
   *
   * Держится над нижней навигацией и не уезжает при прокрутке переписки —
   * иначе, чтобы ответить, приходится долистывать длинный диалог до конца.
   */
  footer?: ReactNode;
}

/**
 * Каркас экрана: шапка, прокручиваемая область и нижняя навигация.
 *
 * Высота считается от --bd-viewport-height, который обновляет ThemeProvider из
 * данных моста: в Telegram и MAX окно может менять высоту при открытии
 * клавиатуры, а 100vh в WebView отдаёт неверное значение.
 */
export function Screen({ children, tab, flush, className, fill, footer, ...appBarProps }: ScreenProps) {
  useBackButton(Boolean(appBarProps.back), appBarProps.backTo);

  // Каждый новый экран открывается сверху, а не с позиции предыдущего
  useEffect(() => {
    document.getElementById('bd-screen-scroll')?.scrollTo({ top: 0 });
  }, []);

  return (
    <div
      className="mx-auto flex w-full max-w-[520px] flex-col overflow-hidden"
      style={{ height: 'var(--bd-viewport-height)' }}
    >
      <AppBar {...appBarProps} />
      <m.main
        id="bd-screen-scroll"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          'bd-scroll min-h-0 flex-1 pt-4',
          flush ? 'px-0' : 'px-3.5',
          fill ? 'flex flex-col' : '',
          className,
        )}
        style={{
          // С закреплённой областью низ занимает она, и запас под навигацию не нужен
          paddingBottom: footer
            ? '16px'
            : tab
              ? 'calc(var(--bd-tabbar-height) + var(--bd-safe-bottom) + 20px)'
              : 'calc(var(--bd-safe-bottom) + 24px)',
        }}
      >
        {children}
      </m.main>
      {footer ? (
        <div
          className="shrink-0 border-t border-line/70 bg-canvas px-3.5 pt-2.5"
          // Нижняя навигация закреплена поверх содержимого, поэтому под неё
          // резервируется место: иначе поле ввода уезжает под панель вкладок
          style={{
            paddingBottom: tab
              ? 'calc(var(--bd-tabbar-height) + var(--bd-safe-bottom) + 8px)'
              : 'calc(var(--bd-safe-bottom) + 12px)',
          }}
        >
          {footer}
        </div>
      ) : null}
      {tab ? <TabBar active={tab} /> : null}
    </div>
  );
}

/** Закреплённая внизу контента группа основных действий. */
export function ActionStack({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mt-3.5 grid gap-2', className)}>{children}</div>;
}
